/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { Divider, IconButton, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import fuzzysort from 'fuzzysort';
import type { TranscriptionResponseDTO } from '@klangio/ai2notes-ts-client';
import SortByMenu from '../../../molecules/SortByMenu';
import SongbookItem from '../SongbookItem/SongbookItem';
import VirtualizedListWrapper from './VirtualizedListWrapper';
import { TrackingAction, TrackingCategory, trackingService } from '../../../../services';

export enum SortOption {
  ByTitle = 'home.songbook.sortby.sort_by_title',
  ByCreationDate = 'home.songbook.sortby.sort_by_creation_date',
}

const useStyles = makeStyles()((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '2em',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '4.5em',
    },
  },

  topContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },

  searchField: {
    margin: '1rem 0',
    flex: '1 1 20rem',
  },

  listContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },

  emptyContainer: {
    gap: '1em',
    marginTop: '2rem',
    padding: '2rem',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },

  songlistContainer: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    colorScheme: 'dark',
    position: 'relative',
    height: 'calc(100vh - 340px)',
    overflow: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    [theme.breakpoints.down('md')]: {
      height: '100vh',
      overflow: 'hidden',
    },
  },
}));

export interface SongbookProps {
  transcriptions?: TranscriptionResponseDTO[];
}

const Songbook: React.FC<SongbookProps> = ({ transcriptions = [] }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [sortOption, setSortOption] = useState<SortOption>(SortOption.ByCreationDate);
  const [sortAscending, setSortAscending] = useState({
    [SortOption.ByTitle]: true,
    [SortOption.ByCreationDate]: false,
  });
  const onAscendingChange = useCallback(
    (value: boolean) => {
      setSortAscending((prevState) => ({
        ...prevState,
        [sortOption]: value,
      }));
    },
    [sortOption, setSortAscending]
  );

  const [filterString, setFilterString] = useState<string>('');

  const sortOptions = useMemo(
    () =>
      Object.keys(SortOption).map((k) => ({
        // @ts-ignore
        key: SortOption[k],
        // @ts-ignore
        label: t(SortOption[k]),
      })),
    [t]
  );

  const { data, searchResults } = useMemo(() => {
    let displayData = [...transcriptions];
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let searchResults: Fuzzysort.KeyResults<TranscriptionResponseDTO> | undefined;

    if (filterString && filterString.trim() !== '') {
      searchResults = fuzzysort.go(filterString, displayData, {
        key: 'title',
      });
      displayData = searchResults.map((res) => res.obj);
    } else {
      switch (sortOption) {
        case SortOption.ByTitle:
          displayData.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case SortOption.ByCreationDate:
        default:
          displayData.sort((a, b) => +new Date(a.creationDate) - +new Date(b.creationDate));
          break;
      }

      if (!sortAscending[sortOption]) {
        displayData.reverse();
      }
    }

    return {
      data: displayData,
      searchResults,
    };
  }, [transcriptions, sortOption, sortAscending, filterString]);

  if (data.length === 0) {
    return (
      <div className={classes.emptyContainer}>
        <span>{t('home.songbook.no_songs_found')}</span>
      </div>
    );
  }
  return (
    <div className={classes.container} data-testid="songbook">
      {transcriptions.length > 1 && (
        <div className={classes.topContainer}>
          <TextField
            onFocus={() =>
              trackingService.event(
                TrackingCategory.Transcription,
                TrackingAction.SONGBOOK_SEARCH_USED
              )
            }
            className={classes.searchField}
            placeholder={t('home.songbook.search_placeholder')}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment:
                filterString !== '' ? (
                  <IconButton onClick={() => setFilterString('')} size="large">
                    <Clear />
                  </IconButton>
                ) : (
                  ''
                ),
            }}
            onKeyDown={(e) => (e.key === 'Esc' ? setFilterString('') : '')}
            value={filterString}
            onChange={(e) => setFilterString(e.target.value)}
          />

          <SortByMenu
            options={sortOptions}
            selectedOption={sortOption}
            onSelectedOptionChange={(option: SortOption) => setSortOption(option)}
            ascending={sortAscending[sortOption]}
            onAscendingChange={onAscendingChange}
            disabled={filterString !== ''}
          />
        </div>
      )}

      <div className={classes.listContainer}>
        <div className={classes.songlistContainer}>
          <VirtualizedListWrapper
            items={data}
            height="100%"
            renderItem={(transcription: TranscriptionResponseDTO, i: number) => (
              <React.Fragment key={`songbook-item-${transcription.id}`}>
                <SongbookItem
                  key={transcription.id}
                  transcription={transcription}
                  searchResult={searchResults?.[i]}
                />
                {(!searchResults || i < data.length - 1) && (
                  <Divider
                    key={`divider-${i}`}
                    sx={{
                      margin: '0 9px',
                    }}
                  />
                )}
              </React.Fragment>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default Songbook;
